// TermsSheetPDF.jsx
import { Document, StyleSheet, Text, View } from "@react-pdf/renderer";
import { isEmpty, isNil } from "lodash";
import PdfPageBranded from "./components/PdfPageBranded";

// ─── Color Palette ───
const COLORS = {
  primary: "#1a3a5c",
  primaryLight: "#e8eef4",
  accent: "#2c6fbb",
  border: "#d0d7de",
  borderLight: "#e8ecf0",
  headerBg: "#f0f4f8",
  text: "#1a1a1a",
  textSecondary: "#222222",
  textMuted: "#888888",
  white: "#ffffff",
  labelBg: "#f5f7f9",
  highlightBg: "#f0f7ff",
};

const s = StyleSheet.create({
  // Section
  sectionContainer: { marginBottom: 24 },
  sectionHeader: {
    backgroundColor: COLORS.primary,
    paddingVertical: 8,
    paddingHorizontal: 10,
    borderTopLeftRadius: 4,
    borderTopRightRadius: 4,
  },
  sectionTitle: {
    fontSize: 9,
    fontWeight: "bold",
    color: COLORS.white,
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  sectionBody: {
    border: `1px solid ${COLORS.border}`,
    borderTopWidth: 0,
    borderBottomLeftRadius: 4,
    borderBottomRightRadius: 4,
    overflow: "hidden",
  },

  // Field rows
  fieldRow: {
    flexDirection: "row",
    borderBottom: `1px solid ${COLORS.borderLight}`,
    minHeight: 22,
  },
  fieldRowLast: { borderBottomWidth: 0 },
  fieldRowHighlight: { backgroundColor: COLORS.highlightBg },
  fieldLabel: {
    width: "45%",
    backgroundColor: COLORS.labelBg,
    paddingVertical: 5,
    paddingHorizontal: 8,
    borderRight: `1px solid ${COLORS.borderLight}`,
    justifyContent: "center",
  },
  fieldLabelText: {
    fontSize: 7.5,
    fontWeight: 500,
    color: COLORS.textSecondary,
  },
  fieldValue: {
    width: "55%",
    paddingVertical: 5,
    paddingHorizontal: 8,
    justifyContent: "center",
  },
  fieldValueText: {
    fontSize: 8,
    color: COLORS.text,
  },
  fieldValueBold: {
    fontSize: 8.5,
    color: COLORS.primary,
    fontWeight: "bold",
  },
  emptyValue: {
    fontSize: 8,
    color: COLORS.textMuted,
    fontStyle: "italic",
  },

  // Sub-header
  subHeader: {
    backgroundColor: "#ffffff",
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderBottom: `2px solid ${COLORS.border}`,
  },
  subHeaderText: {
    fontSize: 8.5,
    fontWeight: "bold",
    color: COLORS.primary,
  },

  // Property / Guarantor cards
  cardRow: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 6,
    paddingHorizontal: 8,
    borderBottom: `1px solid ${COLORS.borderLight}`,
  },
  cardRowLast: { borderBottomWidth: 0 },
  cardIcon: {
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: COLORS.primaryLight,
    justifyContent: "center",
    alignItems: "center",
    marginRight: 8,
  },
  cardIconText: {
    fontSize: 9,
    fontWeight: "bold",
    color: COLORS.primary,
    textAlign: "center",
  },
  cardPrimary: {
    fontSize: 8,
    fontWeight: "bold",
    color: COLORS.text,
  },
  cardSecondary: {
    fontSize: 7,
    color: COLORS.textMuted,
    marginTop: 1,
  },

  // Badge
  badge: {
    backgroundColor: COLORS.primaryLight,
    paddingVertical: 2,
    paddingHorizontal: 6,
    borderRadius: 3,
    alignSelf: "flex-start",
  },
  badgeText: {
    fontSize: 6.5,
    fontWeight: "bold",
    color: COLORS.primary,
    textTransform: "uppercase",
  },

  // Meta
  metaRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderBottom: `1px solid ${COLORS.borderLight}`,
  },
  metaLabel: {
    fontSize: 7,
    color: COLORS.textMuted,
  },
  metaValue: {
    fontSize: 7.5,
    fontWeight: "bold",
    color: COLORS.text,
  },

  // Disclaimer
  disclaimer: {
    fontSize: 6,
    color: COLORS.textMuted,
    lineHeight: 1.5,
    marginTop: 8,
    paddingHorizontal: 2,
  },
});

// ──────────────────────────────────────────────
// HELPERS
// ──────────────────────────────────────────────

const fmt = (n) => {
  if (isNil(n) || n === "") return null;
  return Number(n).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
};

const fmtPct = (n) => {
  if (isNil(n) || n === "") return null;
  return `${Number(n).toFixed(2)}%`;
};

const fmtDate = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

// ──────────────────────────────────────────────
// FIELD CONFIG (mirrors the UI)
// ──────────────────────────────────────────────

const FIELD_CONFIG = [
  { key: "loanProductType", label: "Loan Product Type" },
  { key: "loanPurpose", label: "Loan Purpose" },
  {
    key: "totalLoanAmount",
    label: "Total Loan Amount",
    format: "currency",
    highlight: true,
  },
  {
    key: "initialLoanAmount",
    label: "Initial Loan Amount",
    format: "currency",
  },
  { key: "purchasePrice", label: "Purchase Price", format: "currency" },
  { key: "asIsValue", label: "As-Is Value", format: "currency" },
  {
    key: "constructionBudget",
    label: "Construction Budget",
    format: "currency",
  },
  {
    key: "constructionHoldback",
    label: "Construction Holdback",
    format: "currency",
  },
  {
    key: "interestRate",
    label: "Interest Rate",
    format: "percent",
    highlight: true,
  },
  { key: "interestType", label: "Interest Type" },
  { key: "loanTerm", label: "Loan Term" },
  { key: "amortization", label: "Amortization" },
  { key: "loanToValue", label: "Loan-to-Value (LTV)", format: "percent" },
  { key: "loanToCost", label: "Loan-to-Cost (LTC)", format: "percent" },
  { key: "arvLtv", label: "ARV LTV", format: "percent" },
  { key: "lienPosition", label: "Lien Position" },
  { key: "occupancy", label: "Occupancy" },
  { key: "propertyType", label: "Property Type" },
  { key: "paymentReserve", label: "Payment Reserve" },
  { key: "exitStrategy", label: "Exit Strategy" },
  { key: "legalFee", label: "Legal Fee", format: "currency" },
  { key: "underwritingFee", label: "Underwriting Fee", format: "currency" },
];

const buildTermFields = (data) => {
  if (!data) return [];
  return FIELD_CONFIG.reduce((acc, { key, label, format, highlight }) => {
    const val = data[key];
    if (isNil(val) || val === "") return acc;
    let display;
    if (format === "currency") display = fmt(val);
    else if (format === "percent") display = fmtPct(val);
    else display = String(val);
    if (display) acc.push({ label, value: display, highlight: !!highlight });
    return acc;
  }, []);
};

// ──────────────────────────────────────────────
// REUSABLE PRIMITIVES
// ──────────────────────────────────────────────

const PdfFormSection = ({ title, children }) => (
  <View style={s.sectionContainer}>
    <View style={s.sectionHeader}>
      <Text style={s.sectionTitle}>{title}</Text>
    </View>
    <View style={s.sectionBody}>{children}</View>
  </View>
);

const PdfSubHeader = ({ title }) => (
  <View style={s.subHeader}>
    <Text style={s.subHeaderText}>{title}</Text>
  </View>
);

const PdfField = ({ label, value, isLast = false, highlight = false }) => (
  <View
    style={[
      s.fieldRow,
      isLast && s.fieldRowLast,
      highlight && s.fieldRowHighlight,
    ]}
  >
    <View style={s.fieldLabel}>
      <Text style={s.fieldLabelText}>{label}</Text>
    </View>
    <View style={s.fieldValue}>
      {isNil(value) || value === "" ? (
        <Text style={s.emptyValue}>—</Text>
      ) : (
        <Text style={highlight ? s.fieldValueBold : s.fieldValueText}>
          {value}
        </Text>
      )}
    </View>
  </View>
);

const PdfCardRow = ({ primary, secondary, initials, isLast = false }) => (
  <View style={[s.cardRow, isLast && s.cardRowLast]}>
    <View style={s.cardIcon}>
      <Text style={s.cardIconText}>{initials || "?"}</Text>
    </View>
    <View>
      <Text style={s.cardPrimary}>{primary}</Text>
      {secondary && <Text style={s.cardSecondary}>{secondary}</Text>}
    </View>
  </View>
);

// ──────────────────────────────────────────────
// SECTION COMPONENTS
// ──────────────────────────────────────────────

const PropertiesSection = ({ properties = [] }) => {
  if (isEmpty(properties)) return null;
  return (
    <PdfFormSection
      title={
        properties.length === 1 ? "Subject Property" : "Subject Properties"
      }
    >
      {properties.map((prop, idx) => (
        <PdfCardRow
          key={prop._id || idx}
          primary={prop?.address?.fullAddress || "No address"}
          secondary={prop?.propertyType || null}
          initials={`P${idx + 1}`}
          isLast={idx === properties.length - 1}
        />
      ))}
    </PdfFormSection>
  );
};

const GuarantorsSection = ({ guarantors = [] }) => {
  if (isEmpty(guarantors)) return null;
  return (
    <PdfFormSection
      title={guarantors.length === 1 ? "Guarantor" : "Guarantors"}
    >
      {guarantors.map((g, idx) => {
        const name = [g.firstName, g.lastName].filter(Boolean).join(" ") || "—";
        const initials =
          `${(g.firstName || "?")[0]}${(g.lastName || "?")[0]}`.toUpperCase();
        return (
          <PdfCardRow
            key={g._id || idx}
            primary={name}
            initials={initials}
            isLast={idx === guarantors.length - 1}
          />
        );
      })}
    </PdfFormSection>
  );
};

const LoanTermsSection = ({ data = {} }) => {
  const fields = buildTermFields(data);
  if (isEmpty(fields)) return null;
  return (
    <PdfFormSection title="Loan Terms">
      {fields.map((f, idx) => (
        <PdfField
          key={f.label}
          label={f.label}
          value={f.value}
          highlight={f.highlight}
          isLast={idx === fields.length - 1}
        />
      ))}
    </PdfFormSection>
  );
};

const MetaSection = ({ termSheet = {} }) => {
  const rows = [
    { label: "Issued Date", value: fmtDate(termSheet.createdDate) },
    { label: "Created By", value: termSheet.createdBy },
    { label: "Status at Creation", value: termSheet.loanStatusAtCreation },
    {
      label: "Status",
      value: termSheet.isAccepted ? "Accepted" : "Pending Review",
    },
  ].filter((r) => r.value);

  if (isEmpty(rows)) return null;

  return (
    <PdfFormSection title="Details">
      {rows.map((r, idx) => (
        <View
          key={r.label}
          style={[s.metaRow, idx === rows.length - 1 && s.fieldRowLast]}
        >
          <Text style={s.metaLabel}>{r.label}</Text>
          <Text style={s.metaValue}>{r.value}</Text>
        </View>
      ))}
    </PdfFormSection>
  );
};

// ──────────────────────────────────────────────
// MAIN DOCUMENT
// ──────────────────────────────────────────────

const TermsSheetPDF = ({ formData = {} }) => {
  const data = formData?.term_sheet_data || formData;
  const properties =
    formData?.subjectProperties || data?.subjectProperties || [];
  const guarantors = data?.guarantors || [];
  const today = new Date().toISOString().split("T")[0];

  const address = properties[0]?.address?.fullAddress || "Term Sheet";

  return (
    <Document>
      <PdfPageBranded
        title={
          formData?.createdDate
            ? `Term Sheet - Issued ${fmtDate(formData.createdDate)}${formData.preliminaryTermSheet ? " · Preliminary" : ""}`
            : `Term Sheet`
        }
        // title={`Term Sheet`}
        logo
        subTitle={address}
        // titleDescription={
        //   formData?.createdDate
        //     ? `Issued ${fmtDate(formData.createdDate)}${formData.preliminaryTermSheet ? " · Preliminary" : ""}`
        //     : null
        // }
        footerDate={today}
        pageNumber={1}
      >
        <PropertiesSection properties={properties} />
        <GuarantorsSection guarantors={guarantors} />
        <LoanTermsSection data={data} />
        <MetaSection termSheet={formData} />

        <Text style={s.disclaimer}>
          This term sheet is provided for informational purposes only and does
          not constitute a binding commitment to lend. All terms are subject to
          satisfactory completion of due diligence, internal credit approval,
          and execution of definitive loan documents. Terms and conditions may
          change during the underwriting process.
        </Text>
      </PdfPageBranded>
    </Document>
  );
};

export default TermsSheetPDF;
